import React, { useState, useEffect, useRef } from 'react';
//import data
import { aboutData } from '../data';
//import motion
import { motion, AnimatePresence, useTransform, useMotionValue, animate, useSpring } from 'framer-motion';
//import variants
import { plateVariants, staggerContainer, fadeIn } from '../variants';



const AnimatedWord = ({ word, offset, animateStrike, trigger, fadeColor }) => {
  // Create a dummy motion value.
  const dummy = useMotionValue(0);
  // Create a spring from the dummy value with your desired parameters.
// No delay in the spring configuration.
const springDummy = useSpring(dummy, { 
  stiffness: 300, 
  damping: 10 
});
// Map the spring value so that when dummy goes from 0 to 40, 
// it overshoots and then settles to -offset.
// For example, when dummy=0 => y=0, dummy=20 => y = -offset - 5 (overshoot),
// dummy=40 => y = -offset.
const yOffset = useTransform(springDummy, [0, 20, 40], [0, -offset - 5, -offset]);

useEffect(() => {
  if (trigger) {
    const timer = setTimeout(() => {
      // Overshoot: set dummy to 40.
      dummy.set(40);
      // Wait a bit longer before reverting.
      const revert = setTimeout(() => {
        dummy.set(30);
      }, 1000); // increased from 300ms to 500ms
      return () => clearTimeout(revert);
    }, 3000); // increased from 500ms to 800ms
    return () => clearTimeout(timer);
  }
}, [trigger, dummy]);

  

  return (
    <motion.div
      initial={{ opacity: 1, y: 0 }}
      animate={{ opacity: 1, y: yOffset }}
      exit={{ opacity: 0 }}
    >
      <span className="relative inline-block">
  <motion.span
    initial={{ color: "#27272a" }}
    animate={fadeColor ? { color: "#7f1d1d" } : {}}
    transition={{ duration: 1 }}
  >
    {word}
  </motion.span>
  {animateStrike && (
    <motion.span
      initial={{ width: 0 }}
      animate={{ width: '120%' }}
      transition={{ type: 'spring', stiffness: 150, damping: 5 }}
      className="absolute left-0 top-1/2 h-[2px] bg-red-900"
      style={{ transform: 'translateY(-50%)' }}
    />
  )}
</span>

    </motion.div>
  );
};

const AnimatedTitle = ({ start }) => {
  const words = ["dynamic", "displayed", "devoted", "easy"];
  const [currentIndex, setCurrentIndex] = useState(0);
  const [animateStrike, setAnimateStrike] = useState(false);

  useEffect(() => {
    if (start && currentIndex < words.length - 1) {
      const timer = setTimeout(() => {
        setAnimateStrike(true);
        setTimeout(() => {
          setCurrentIndex(prev => prev + 1);
          setAnimateStrike(false);
        }, 250);
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [start, currentIndex, words.length]);

  const [fadeColor, setFadeColor] = useState(false); 

  useEffect(() => {
    if (currentIndex === words.length - 1) {
      const timer = setTimeout(() => {
        setFadeColor(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [currentIndex, words.length]);

  return (
    <div className="relative inline-block overflow-hidden" style={{ height: '200px', width: '200px' }}>
      <div className="flex flex-col justify-end h-full ">
        <AnimatePresence>
          {words.map((word, idx) => {
            if (idx > currentIndex) return null;
            // For words that are already bumped, we want a final offset of 1 pixel per step.
            const finalOffset = (currentIndex - idx) * 1;
            const trigger = idx < currentIndex; // Only animate bumped words.
            return (
              <AnimatedWord
              key={word + idx}
              word={word}
              offset={finalOffset}
              animateStrike={(idx < currentIndex || (idx === currentIndex && animateStrike))}
              trigger={trigger}
              fadeColor={idx === words.length - 1 ? fadeColor : false}
              
            />
            );
          })}
        </AnimatePresence>
      </div>
    </div>
  );
};


const About = React.forwardRef(({ scrollToMenu }, ref) => {
  const { pretitle, subtitle, subtitle2, btnText, image } = aboutData;
  
  // State for viewport settings and to detect screen size for both image and text.
  const [viewportAmount, setViewportAmount] = useState(0.6);
  const [viewportMargin, setViewportMargin] = useState("0px");
  const [imageVisibility, setImageVisibility] = useState('large'); // 'large', 'small', or 'none'
  const [isMediumScreen, setIsMediumScreen] = useState(false); // for text block offset
  const [startAnimation, setStartAnimation] = useState(false);

  const [showABC, setShowABC] = useState(false);

  useEffect(() => {
    if (startAnimation) {
      const timer = setTimeout(() => {
        setShowABC(true);
      }, 5500); // tweak this if needed, but this aligns with your current word timing
      return () => clearTimeout(timer);
    }
  }, [startAnimation]);

  useEffect(() => {
    const updateViewport = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setViewportAmount(0.6);
        setViewportMargin("0px");
        setImageVisibility('large');
        setIsMediumScreen(false); // Desktop layout
      } else {
        setViewportAmount(0.3);
        setViewportMargin("0px 0px -30% 0px");
        // For screens under 1024px, always use the mobile column layout:
        if (width < 450) {
          setImageVisibility('none');
        } else {
          setImageVisibility('none');
        }
        setIsMediumScreen(true); // Mobile layout for all screens <1024px
      }
    };
  
    updateViewport();
    window.addEventListener('resize', updateViewport);
    return () => window.removeEventListener('resize', updateViewport);
  }, []);
  
  
  // Render the image container.
  const renderImage = () => {
    if (imageVisibility === 'large') {
      return (
        <motion.div
          variants={plateVariants}
          className="-mb-[80px] -mr-[180px] z-10 lg:mr-[calc(-100vw/2+450px)] xl:mr-[calc(-100vw/2+700px)] xl:justify-end"
        >
          <img 
            src={image}
            alt=""
            className="w-full"
            style={{ maxWidth: '400px' }}
          />
        </motion.div>
      );
    } else if (imageVisibility === 'small') {
      return (
        <motion.div
          variants={plateVariants}
          className="mb-[100px] -mr-[180px] z-10 -mt-[150px]"
        >
          <img 
            src={image}
            alt=""
            className="w-full"
            style={{ maxWidth: '400px', maxHeight: '300px' }}
          />
        </motion.div>
      );
    } else {
      return null;
    }
  };

  const renderTextBlock = () => {
    const headerContent = isMediumScreen ? (
      <h2 className="h2 text-[32px] text-stone-800 capitalize text-center">
        <div className="flex flex-col items-center">
          <AnimatedTitle start={startAnimation} />
          <span className=" inline-block">dining data</span>
          <div>
          <div className="h-[24px]">
  <AnimatePresence>
    {showABC && (
      <motion.span
        key="abc-mobile"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -10 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className="text-red-900 text-[20px] inline-block"
      >
        As A, B, C
      </motion.span>
    )}
  </AnimatePresence>
</div>
          </div>
        </div>
      </h2>
    ) : (
      <h2 className="h2 text-[40px] text-stone-800 capitalize">
        <div className="flex flex-row items-end">
          <div className="flex flex-col items-start">
            <AnimatedTitle start={startAnimation} />
            <span className=" inline-block">dining data</span>
          </div>
          <div className="ml-4">
          <AnimatePresence>
  {showABC && (
    <motion.span
      key="abc-desktop"
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -10 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className="text-red-900 text-[20px] inline-block"
    >
      As A, B, C
    </motion.span>
  )}
</AnimatePresence>

          </div>
        </div>
      </h2>
    );
  
    const content = (
      <>
        {headerContent}
        <p className="mb-4 text-[#fcfcf1] max-w-[560px] xl:text-[22px]" style={{ textShadow: "0 0 16px #292524, 0 0 36px #292524" }}>
         GoHost combines the best of POS and reservation companies, into one unified system that throws away the add-on model.
        </p>
        <p className="mb-4 text-[#fcfcf1] max-w-[560px] xl:text-[22px]" style={{ textShadow: "0 0 16px #292524, 0 0 36px #292524" }}>
What others bolt on and bill for - staffing, payroll engines, websites, online ordering, reservations, gift cards and SMS notifications - we see as essentials to your success. It’s all included.        </p>
        <p className="mb-8 text-[#fcfcf1] max-w-[560px] xl:text-[22px]" style={{ textShadow: "0 0 16px #292524, 0 0 36px #292524" }}>
No monthly fees, no add-ons, no gotchas. GoHost only wins when you win. </p>
        <button className="btn bg-yellow-600 capitalize mx-auto lg:mx-0" onClick={scrollToMenu}>
          See All Features
        </button>
      </>
    );
  
    if (isMediumScreen) {
      return (
        <motion.div 
          variants={fadeIn('right', 'tween', 0.1, 1.2)}
          style={{ marginTop: '-100px', minHeight: '550px' }}
          className="flex-1 text-center mb-[100px]"
        >
          {content}
        </motion.div>
      );
    } else {
      return (
        <motion.div 
          variants={fadeIn('right', 'tween', 0.1, 1.2)}
          className="flex-1 text-center lg:text-left"
        >
          {content}
        </motion.div>
      );
    }
  };
  
  return (
    <section ref={ref} className="  select-none bg-gradient-to-b from-[#ead05a] to-red-900 pb-[150px]">
      <div className="container mx-auto ">
        <motion.div 
          variants={staggerContainer}
          initial="hidden"
          onViewportEnter={() => setStartAnimation(true)}
          whileInView="show"
          viewport={{ once: false, amount: viewportAmount, margin: viewportMargin }}
          className="flex flex-col lg:flex-row items-center"
        >
          {renderTextBlock()}
          {renderImage()}
        </motion.div>
      </div>
    </section>
  );
});

export default About;
